/**
 * Product media gallery — Splide stage + Splide thumb strip (same pairing as the
 * Bulldog Swiper gallery: main slider and a thumbnail slider kept in step).
 * Stage: one slide per swipe, native drag with resistance, smooth transitions;
 * arrows, keyboard, thumbs and variant changes all drive it through go().
 * Thumbs: free-drag strip (wheel on the desktop column). The active thumb is
 * pinned to the start of the strip (top of a side column, left of the row)
 * once the rail has a real size, including after a variant deep-link.
 * Lightbox keeps CSS scroll-snap (wheel / drag / thumbs).
 * Hosted video autoplays muted when active; external iframes pause on leave
 * (stage + lightbox).
 * 3D models stay poster-first until "View in 360" loads model-viewer on stage
 * or in the desktop lightbox (same deferred activate + snap lock).
 * Desktop lightbox is a fullscreen <dialog> takeover (trigger + dialog ≥750px).
 * Open via expand control or double-click on the stage (image / 3D poster).
 * The stage always slides horizontally, matching the side arrows. Thumbnail
 * placement left/right is desktop-only and stacks the thumbs in a column;
 * mobile always has thumbs below (no zoom chrome). Mobile: custom pinch zooms the
 * active image only; vertical page scroll is not trapped by the stage. Stage
 * drag pauses while an image is zoomed or a 3D model is active.
 */
(() => {
  const TAG = 'product-media-gallery';
  const DESKTOP_LIGHTBOX_MQ = '(min-width: 750px)';
  const IMAGE_ZOOM_MIN = 1;
  const IMAGE_ZOOM_MAX = 3;
  // A click this soon after a drag is its release, not half of a double-click
  const DRAG_CLICK_GUARD_MS = 400;
  const SLIDE_SPEED_MS = 400;
  // Splide keeps tab order in step with visibility for these; the thumbs manage
  // their own roving tabindex, so they opt out with a selector that never matches
  const NO_FOCUSABLE_NODES = '[data-gallery-none]';

  if (customElements.get(TAG)) return;

  class ProductMediaGallery extends HTMLElement {
    #activeIndex = 0;
    #lightboxIndex = 0;
    #lightboxIgnoreScrollSync = false;
    #lightboxScrollSyncTimer = 0;
    #lightboxObserver = null;
    #lightboxDrag = null;
    #modelViewerUIs = new WeakMap();
    #desktopLightboxMq = null;
    #dialogEl = null;
    #dialogHome = null;
    #lightboxClosing = false;
    #imageZoom = { scale: 1, x: 0, y: 0 };
    #pinch = null;
    #panZoom = null;
    #externalPlayCleanups = new WeakMap();
    #stageSlider = null;
    #thumbSlider = null;
    #thumbsVertical = null;
    #splideWait = 0;
    #stageDragStartIndex = null;
    #stageDragEndedAt = 0;
    #thumbRailObserver = null;
    #thumbRevealFrame = 0;
    #thumbRevealIndex = -1;
    #thumbRevealAttempts = 0;

    connectedCallback() {
      const initialIndex = Math.max(0, Number(this.dataset.activeIndex || 0));

      this.#activeIndex = initialIndex;
      this.#desktopLightboxMq = window.matchMedia(DESKTOP_LIGHTBOX_MQ);
      this.#desktopLightboxMq.addEventListener('change', this.#onDesktopLightboxMqChange);
      this.#dialogEl = this.querySelector('[data-gallery-dialog]');
      this.#dialogHome = this.#dialogEl?.parentElement ?? this;

      this.#prev()?.addEventListener('click', this.#onPrev);
      this.#next()?.addEventListener('click', this.#onNext);
      this.#zoom()?.addEventListener('click', this.#onZoom);
      this.#view360()?.addEventListener('click', this.#onView360);
      this.#stage()?.addEventListener('dblclick', this.#onStageDblClick);
      this.#thumbsContainer()?.addEventListener('click', this.#onThumbClick);
      this.#thumbsContainer()?.addEventListener('keydown', this.#onThumbKeydown);
      this.#dialog()?.addEventListener('close', this.#onDialogClose);
      this.#dialog()?.addEventListener('cancel', this.#onDialogCancel);
      this.#dialog()?.addEventListener('keydown', this.#onDialogKeydown);
      this.#dialogClose()?.addEventListener('click', this.#onDialogCloseClick);
      this.#dialogView360()?.addEventListener('click', this.#onDialogView360);
      this.#dialogThumbs()?.addEventListener('click', this.#onDialogThumbClick);
      this.#dialogThumbs()?.addEventListener('keydown', this.#onDialogThumbKeydown);

      const viewport = this.#viewport();
      const lightboxViewport = this.#dialogViewport();

      if (viewport) {
        viewport.addEventListener('keydown', this.#onViewportKeydown);
        viewport.addEventListener('touchstart', this.#onTouchStart, { passive: false });
        viewport.addEventListener('touchmove', this.#onTouchMove, { passive: false });
        viewport.addEventListener('touchend', this.#onTouchEnd);
        viewport.addEventListener('touchcancel', this.#onTouchEnd);
      }

      if (lightboxViewport) {
        lightboxViewport.addEventListener('pointerdown', this.#onLightboxPointerDown);
        lightboxViewport.addEventListener('pointermove', this.#onLightboxPointerMove);
        lightboxViewport.addEventListener('pointerup', this.#onLightboxPointerUp);
        lightboxViewport.addEventListener('pointercancel', this.#onLightboxPointerUp);
        lightboxViewport.addEventListener('lostpointercapture', this.#onLightboxPointerUp);
        lightboxViewport.addEventListener('scrollend', this.#onLightboxScrollEnd);
        lightboxViewport.addEventListener('keydown', this.#onLightboxViewportKeydown);
        this.#observeLightboxSlides(lightboxViewport);
      }

      this.#setActive(initialIndex);
      this.#whenSplideReady(() => this.#mountSliders());
    }

    /**
     * Public API — jump stage + thumbs to the slide for a product media id
     * (e.g. selected variant featured_media).
     * @param {string | number} mediaId
     */
    selectByMediaId(mediaId) {
      if (mediaId == null || mediaId === '') return;

      const slides = this.#slides();
      const index = slides.findIndex(
        (slide) => String(slide.dataset.mediaId) === String(mediaId)
      );

      if (index < 0 || index === this.#activeIndex) return;

      this.#select(index, { scrollStage: true });
    }

    disconnectedCallback() {
      this.#desktopLightboxMq?.removeEventListener('change', this.#onDesktopLightboxMqChange);
      this.#prev()?.removeEventListener('click', this.#onPrev);
      this.#next()?.removeEventListener('click', this.#onNext);
      this.#zoom()?.removeEventListener('click', this.#onZoom);
      this.#view360()?.removeEventListener('click', this.#onView360);
      this.#stage()?.removeEventListener('dblclick', this.#onStageDblClick);
      this.#thumbsContainer()?.removeEventListener('click', this.#onThumbClick);
      this.#thumbsContainer()?.removeEventListener('keydown', this.#onThumbKeydown);
      this.#dialog()?.removeEventListener('close', this.#onDialogClose);
      this.#dialog()?.removeEventListener('cancel', this.#onDialogCancel);
      this.#dialog()?.removeEventListener('keydown', this.#onDialogKeydown);
      this.#dialogClose()?.removeEventListener('click', this.#onDialogCloseClick);
      this.#dialogView360()?.removeEventListener('click', this.#onDialogView360);
      this.#dialogThumbs()?.removeEventListener('click', this.#onDialogThumbClick);
      this.#dialogThumbs()?.removeEventListener('keydown', this.#onDialogThumbKeydown);

      const viewport = this.#viewport();
      const lightboxViewport = this.#dialogViewport();

      if (viewport) {
        viewport.removeEventListener('keydown', this.#onViewportKeydown);
        viewport.removeEventListener('touchstart', this.#onTouchStart);
        viewport.removeEventListener('touchmove', this.#onTouchMove);
        viewport.removeEventListener('touchend', this.#onTouchEnd);
        viewport.removeEventListener('touchcancel', this.#onTouchEnd);
      }

      if (lightboxViewport) {
        lightboxViewport.removeEventListener('pointerdown', this.#onLightboxPointerDown);
        lightboxViewport.removeEventListener('pointermove', this.#onLightboxPointerMove);
        lightboxViewport.removeEventListener('pointerup', this.#onLightboxPointerUp);
        lightboxViewport.removeEventListener('pointercancel', this.#onLightboxPointerUp);
        lightboxViewport.removeEventListener('lostpointercapture', this.#onLightboxPointerUp);
        lightboxViewport.removeEventListener('scrollend', this.#onLightboxScrollEnd);
        lightboxViewport.removeEventListener('keydown', this.#onLightboxViewportKeydown);
      }

      this.#restoreDialogHome();
      window.clearInterval(this.#splideWait);
      this.#cancelThumbReveal();
      this.#thumbRailObserver?.disconnect();
      this.#thumbRailObserver = null;
      this.#thumbSlider?.destroy();
      this.#thumbSlider = null;
      this.#stageSlider?.destroy();
      this.#stageSlider = null;
      this.#lightboxObserver?.disconnect();
      this.#lightboxObserver = null;
      window.clearTimeout(this.#lightboxScrollSyncTimer);
      this.#pauseAllMedia();
      this.#deactivateAllModels();
      this.#resetImageZoom();
    }

    #stage() {
      return this.querySelector('[data-gallery-stage]');
    }

    #viewport() {
      return this.querySelector('[data-gallery-viewport]');
    }

    #thumbsContainer() {
      return this.querySelector('[data-gallery-thumbs]');
    }

    #thumbs() {
      return [...(this.#thumbsContainer()?.querySelectorAll('[data-gallery-thumb]') || [])];
    }

    #slides() {
      return [...(this.#viewport()?.querySelectorAll('[data-gallery-slide]') || [])];
    }

    #prev() {
      return this.querySelector('[data-gallery-prev]');
    }

    #next() {
      return this.querySelector('[data-gallery-next]');
    }

    #zoom() {
      return this.querySelector('[data-gallery-lightbox]');
    }

    #view360() {
      return this.querySelector('[data-gallery-view-360]');
    }

    #dialog() {
      return this.#dialogEl;
    }

    #dialogClose() {
      return this.#dialog()?.querySelector('[data-gallery-dialog-close]');
    }

    #dialogView360() {
      return this.#dialog()?.querySelector('[data-gallery-dialog-view-360]');
    }

    #dialogThumbs() {
      return this.#dialog()?.querySelector('[data-gallery-dialog-thumbs]');
    }

    #dialogThumbsList() {
      return [...(this.#dialogThumbs()?.querySelectorAll('[data-gallery-dialog-thumb]') || [])];
    }

    #dialogViewport() {
      return this.#dialog()?.querySelector('[data-gallery-dialog-viewport]');
    }

    #dialogSlides() {
      return [...(this.#dialogViewport()?.querySelectorAll('[data-gallery-dialog-slide]') || [])];
    }

    #isDesktopLightbox() {
      return this.#desktopLightboxMq?.matches ?? window.matchMedia(DESKTOP_LIGHTBOX_MQ).matches;
    }

    #onDesktopLightboxMqChange = () => {
      // CSS hides the dialog below 750px; force-close so showModal() does not trap the page.
      if (!this.#isDesktopLightbox()) {
        const dialog = this.#dialog();

        if (dialog?.open) {
          this.#lightboxClosing = false;
          dialog.classList.remove('is-open', 'is-closing');
          dialog.close();
        }
      }

      // Left/right thumbs move below the stage at the same breakpoint, so the
      // strip changes direction; rebuild it like the Bulldog gallery does.
      this.#mountThumbs();
    };

    #isVerticalThumbs() {
      // Placement left/right is desktop-only; mobile always has a thumb row below.
      if (!this.#isDesktopLightbox()) return false;

      return (
        this.classList.contains('product-media-gallery--thumbs-left') ||
        this.classList.contains('product-media-gallery--thumbs-right')
      );
    }

    #select(index, { focus = false, scrollStage = true } = {}) {
      const slides = this.#slides();

      if (!slides.length) return;

      const nextIndex = Math.max(0, Math.min(slides.length - 1, Math.floor(Number(index) || 0)));
      const slide = slides[nextIndex];

      if (!slide) return;

      this.#setActive(nextIndex, { focus });

      // Splide animates straight to the slide, however far away it is
      if (scrollStage && this.#stageSlider && this.#stageSlider.index !== nextIndex) {
        this.#stageSlider.go(nextIndex);
      }
    }

    #setActive(index, { focus = false } = {}) {
      const slides = this.#slides();
      const slide = slides[index];

      if (!slide) return;

      const previousIndex = this.#activeIndex;

      this.#activeIndex = index;
      this.dataset.activeIndex = String(index);

      for (const item of slides) {
        item.classList.toggle('is-active', Number(item.dataset.index) === index);
      }

      if (previousIndex !== index) {
        this.#deactivateModel(slides[previousIndex]);
        this.#resetImageZoom();
      }

      this.#syncThumbs(this.#thumbs(), index, focus);
      this.#syncView360(slide);
      this.#syncLightboxTrigger(slide);
      this.#syncNavButtons();
      this.#syncMediaPlayback(index);
    }

    #syncThumbs(thumbs, activeIndex, focus) {
      for (const thumb of thumbs) {
        const isActive = Number(thumb.dataset.index) === activeIndex;

        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        thumb.tabIndex = isActive ? 0 : -1;

        if (isActive) {
          // Keyboard focus must land after the thumb is in view. An outside
          // outline on a thumb parked at the far edge sits below the fold, and
          // the browser then scrolls the page to chase it.
          this.#revealThumb(activeIndex, { instant: focus });

          if (focus) {
            thumb.focus({ preventScroll: true });
          }
        }
      }
    }

    /**
     * Mount the stage and thumb sliders. The stage drives everything through its
     * move event (drag, arrows, keys, thumbs and variant changes alike); the
     * thumb strip is paired by hand rather than with Splide's sync(), which
     * re-pins the strip to the active thumb on every change.
     */
    #mountSliders() {
      const viewport = this.#viewport();

      if (!viewport || !window.Splide || this.#stageSlider) return;

      const stage = new window.Splide(viewport, {
        type: 'slide',
        perPage: 1,
        perMove: 1,
        start: this.#activeIndex,
        speed: SLIDE_SPEED_MS,
        arrows: false,
        pagination: false,
        keyboard: false,
        rewind: false,
        drag: this.#slides().length > 1,
        dragMinThreshold: { mouse: 4, touch: 10 },
      });

      stage.on('drag', () => {
        this.#stageDragStartIndex = stage.index;
      });

      stage.on('dragged', () => {
        this.#stageDragEndedAt = performance.now();
        // The release calls go() right after this event; clear once it has
        queueMicrotask(() => {
          this.#stageDragStartIndex = null;
        });
      });

      stage.on('move', (index) => {
        if (index !== this.#activeIndex) this.#setActive(index);
      });

      stage.mount();
      this.#limitDragToOneSlide(stage);
      this.#stageSlider = stage;
      this.#syncStageDrag();
      this.#mountThumbs();
    }

    /**
     * A full-width slide plus a quick flick can carry a swipe two slides on.
     * Remember where a drag started and let its release move one slide at most
     * (same rule as carousel.js). Splide's drag hands its target to
     * Controller.go(); arrows, keys and thumbs never pass through a drag.
     */
    #limitDragToOneSlide(splide) {
      const controller = splide.Components.Controller;
      const go = controller.go;

      controller.go = (control, allowSameIndex, callback) => {
        let target = control;

        if (this.#stageDragStartIndex !== null && typeof control === 'number') {
          target = this.#stageDragStartIndex + Math.sign(control - this.#stageDragStartIndex);
        }

        return go.call(controller, target, allowSameIndex, callback);
      };
    }

    #mountThumbs() {
      const container = this.#thumbsContainer();

      if (!container || !window.Splide) return;

      const vertical = this.#isVerticalThumbs();

      if (this.#thumbSlider && this.#thumbsVertical === vertical) return;

      this.#thumbSlider?.destroy();

      const gap =
        parseFloat(getComputedStyle(this).getPropertyValue('--product-media-gallery-gap')) || 8;

      const thumbs = new window.Splide(container, {
        direction: vertical ? 'ttb' : 'ltr',
        height: vertical ? '100%' : undefined,
        autoWidth: !vertical,
        autoHeight: vertical,
        gap: `${gap}px`,
        padding: vertical ? { top: '1px', bottom: '1px' } : { left: '8px', right: '8px' },
        drag: 'free',
        snap: false,
        arrows: false,
        pagination: false,
        keyboard: false,
        wheel: vertical,
        releaseWheel: true,
        trimSpace: 'move',
        focusableNodes: NO_FOCUSABLE_NODES,
      });

      thumbs.mount();
      this.#thumbSlider = thumbs;
      this.#thumbsVertical = vertical;
      this.#thumbRevealAttempts = 0;
      this.#revealThumb(this.#activeIndex, { instant: true });
      this.#watchThumbRail();
    }

    /**
     * Re-pin when the rail gets its height. The first measurement can run
     * before the stage image sizes the column, and the strip then stays on
     * its start index.
     */
    #watchThumbRail() {
      const rail = this.querySelector('.product-media-gallery__thumbs-rail');

      if (!rail || this.#thumbRailObserver) return;

      this.#thumbRailObserver = new ResizeObserver(() => {
        this.#revealThumb(this.#activeIndex, { instant: true });
      });
      this.#thumbRailObserver.observe(rail);
    }

    #cancelThumbReveal() {
      window.cancelAnimationFrame(this.#thumbRevealFrame);
      this.#thumbRevealFrame = 0;
    }

    #scheduleThumbReveal(index, options) {
      if (this.#thumbRevealAttempts >= 30) return;

      this.#thumbRevealAttempts += 1;
      this.#cancelThumbReveal();
      this.#thumbRevealIndex = index;
      this.#thumbRevealFrame = window.requestAnimationFrame(() => {
        this.#thumbRevealFrame = 0;

        // The active slide can change while this frame is waiting. Running it
        // anyway calls go() for the thumb we already left and rewinds the strip.
        if (index !== this.#activeIndex) return;

        this.#revealThumb(index, options);
      });
    }

    /**
     * Put the active thumb at the start of the strip: the top of a side
     * column, or the left of the row. Near the end of the list Splide's
     * trimSpace keeps the last thumbs in view instead of leaving a gap.
     */
    #revealThumb(index, { instant = false } = {}) {
      const slider = this.#thumbSlider;

      if (!slider) {
        this.#cancelThumbReveal();
        return;
      }

      const maxIndex = Math.max(0, slider.length - 1);
      const safeIndex = Math.max(0, Math.min(maxIndex, Math.floor(Number(index) || 0)));

      // Click and variant changes return once go() has run, without scheduling
      // another frame. A reveal still queued for the previous thumb would then
      // pin that old index. Drop it first. A retry of this same thumb keeps its
      // attempt count; a different thumb starts again.
      if (this.#thumbRevealFrame && this.#thumbRevealIndex !== safeIndex) {
        this.#thumbRevealAttempts = 0;
      }

      this.#cancelThumbReveal();

      const track = slider.Components.Elements.track;
      const slide = slider.Components.Slides.getAt(safeIndex)?.slide;

      if (!track || !slide) {
        this.#scheduleThumbReveal(safeIndex, { instant });
        return;
      }

      const vertical = this.#thumbsVertical;
      const start = vertical ? 'top' : 'left';
      const end = vertical ? 'bottom' : 'right';
      const size = vertical ? 'height' : 'width';
      const trackRect = track.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();

      if (trackRect[size] < 8 || slideRect[size] < 8) {
        this.#scheduleThumbReveal(safeIndex, { instant });
        return;
      }

      // Visible-but-not-leading is the bug: the variant thumb sat at the
      // bottom of the column, so "already in view" must not count.
      if (this.#thumbLeads(slideRect, trackRect, start)) {
        this.#thumbRevealAttempts = 0;
        return;
      }

      const positionBefore = slider.Components.Move.getPosition();
      const speed = slider.options.speed;

      if (instant) slider.options.speed = 0;

      // allowSameIndex: a go() while the slides had no offsets can record the
      // right index and still leave the strip at the start. A later go() to
      // that same index would otherwise no-op.
      slider.go(safeIndex, true);

      if (instant) slider.options.speed = speed;

      if (!instant) {
        this.#thumbRevealAttempts = 0;
        return;
      }

      const nextSlide = slide.getBoundingClientRect();
      const nextTrack = track.getBoundingClientRect();
      const moved = Math.abs(slider.Components.Move.getPosition() - positionBefore) >= 1;

      if (this.#thumbLeads(nextSlide, nextTrack, start)) {
        this.#thumbRevealAttempts = 0;
        return;
      }

      // The tail of the list cannot reach the start (Splide trimSpace). Being
      // fully inside the window is enough there. Anywhere earlier, a thumb
      // that is only "in view" still has to move to the start. The last index
      // that can lead is maxIndex - fits + 1: that many thumbs, plus the gap
      // between them, are what fill the track.
      const inside =
        nextSlide[start] >= nextTrack[start] - 1 && nextSlide[end] <= nextTrack[end] + 1;
      const gap =
        parseFloat(getComputedStyle(this).getPropertyValue('--product-media-gallery-gap')) || 0;
      const trackStyle = getComputedStyle(track);
      const padStart = parseFloat(trackStyle[vertical ? 'paddingTop' : 'paddingLeft']) || 0;
      const padEnd = parseFloat(trackStyle[vertical ? 'paddingBottom' : 'paddingRight']) || 0;
      const box = vertical ? track.clientHeight : track.clientWidth;
      const visible = Math.max(0, box - padStart - padEnd);
      const step = Math.max(nextSlide[size] + gap, 1);
      const fits = Math.max(1, Math.floor((visible + gap) / step));
      const cannotLead = safeIndex > maxIndex - fits + 1;

      if (!moved && inside && cannotLead) {
        this.#thumbRevealAttempts = 0;
        return;
      }

      this.#scheduleThumbReveal(safeIndex, { instant });
    }

    /**
     * 16px covers the strip's start padding (8px on the row, 1px on the column).
     */
    #thumbLeads(slideRect, trackRect, start) {
      return Math.abs(slideRect[start] - trackRect[start]) <= 16;
    }

    /** Stage drag pauses while a 3D model is active, an image is zoomed or a pinch is under way. */
    #syncStageDrag() {
      const drag = this.#stageSlider?.Components.Drag;

      if (!drag) return;

      drag.disable(
        this.classList.contains('is-model-active') ||
          this.classList.contains('is-image-zoomed') ||
          Boolean(this.#pinch)
      );
    }

    /** splide.min.js loads deferred alongside this module; wait for it briefly. */
    #whenSplideReady(callback) {
      if (window.Splide) {
        callback();
        return;
      }

      let tries = 0;

      this.#splideWait = window.setInterval(() => {
        if (!window.Splide && tries++ < 100) return;

        window.clearInterval(this.#splideWait);
        if (window.Splide) callback();
      }, 50);
    }

    /**
     * Move a slide or thumb inside its own scroller.
     * scrollIntoView walks every ancestor, including the page, so a tall
     * gallery slide jumps the PDP on load and again on variant change.
     * `start` pins the child to the scroller origin (stage slides).
     * `nearest` only scrolls when the child is outside the visible area.
     */
    #alignInScroller(scroller, child, { behavior = 'auto', align = 'start' } = {}) {
      if (!scroller || !child) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const scrollerRect = scroller.getBoundingClientRect();
      const childRect = child.getBoundingClientRect();
      let deltaTop = 0;
      let deltaLeft = 0;

      if (align === 'nearest') {
        if (childRect.top < scrollerRect.top) {
          deltaTop = childRect.top - scrollerRect.top;
        } else if (childRect.bottom > scrollerRect.bottom) {
          deltaTop = childRect.bottom - scrollerRect.bottom;
        }

        if (childRect.left < scrollerRect.left) {
          deltaLeft = childRect.left - scrollerRect.left;
        } else if (childRect.right > scrollerRect.right) {
          deltaLeft = childRect.right - scrollerRect.right;
        }
      } else {
        // Respect the scroller's scroll-padding (the thumb track insets). Snap
        // points use it too, so a target that ignored it would snap elsewhere.
        const styles = window.getComputedStyle(scroller);

        deltaTop = childRect.top - scrollerRect.top - (parseFloat(styles.scrollPaddingTop) || 0);
        deltaLeft = childRect.left - scrollerRect.left - (parseFloat(styles.scrollPaddingLeft) || 0);
      }

      if (Math.abs(deltaTop) < 1 && Math.abs(deltaLeft) < 1) return;

      scroller.scrollTo({
        top: scroller.scrollTop + deltaTop,
        left: scroller.scrollLeft + deltaLeft,
        behavior: reduceMotion ? 'auto' : behavior,
      });
    }

    #syncView360(slide) {
      const button = this.#view360();

      if (!button) return;

      const modelRoot = slide?.querySelector('[data-gallery-model]');
      const show = Boolean(
        slide?.dataset.mediaType === 'model' && modelRoot && !modelRoot.classList.contains('is-active')
      );

      button.hidden = !show;
      button.classList.toggle('is-hidden', !show);
    }

    #syncDialogView360(slide) {
      const button = this.#dialogView360();

      if (!button) return;

      const modelRoot = slide?.querySelector('[data-gallery-model]');
      const show = Boolean(
        slide?.dataset.mediaType === 'model' && modelRoot && !modelRoot.classList.contains('is-active')
      );

      button.hidden = !show;
      button.classList.toggle('is-hidden', !show);
    }

    #syncLightboxTrigger(slide) {
      const button = this.#zoom();

      if (!button) return;

      const type = slide?.dataset.mediaType || '';
      const hide = type === 'video' || type === 'external_video';

      button.hidden = hide;
      button.classList.toggle('is-hidden', hide);
    }

    #syncNavButtons() {
      const slides = this.#slides();
      const atStart = this.#activeIndex <= 0;
      const atEnd = this.#activeIndex >= slides.length - 1;
      const prev = this.#prev();
      const next = this.#next();

      if (prev) {
        prev.disabled = atStart || slides.length < 2;
      }

      if (next) {
        next.disabled = atEnd || slides.length < 2;
      }
    }

    #syncMediaPlayback(activeIndex, slides = this.#slides()) {
      for (const slide of slides) {
        const isActive = Number(slide.dataset.index) === activeIndex;
        const video = slide.querySelector('video');
        const iframe = slide.querySelector('[data-gallery-external-video]');

        if (video) {
          if (isActive) {
            video.muted = true;
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        }

        if (iframe) {
          if (isActive) {
            this.#playExternalVideo(iframe);
          } else {
            this.#pauseExternalVideo(iframe);
          }
        }
      }
    }

    #pauseAllMedia() {
      this.#pauseMediaInSlides(this.#slides());
      this.#pauseMediaInSlides(this.#dialogSlides());
    }

    #pauseMediaInSlides(slides) {
      for (const slide of slides) {
        slide.querySelector('video')?.pause();
        const iframe = slide.querySelector('[data-gallery-external-video]');

        if (iframe) {
          this.#pauseExternalVideo(iframe);
        }
      }
    }

    #isExternalVideoActive(iframe) {
      const slide =
        iframe.closest('[data-gallery-dialog-slide]') || iframe.closest('[data-gallery-slide]');

      if (!slide) return false;

      const index = Number(slide.dataset.index);

      if (Number.isNaN(index)) return false;

      if (slide.matches('[data-gallery-dialog-slide]')) {
        return Boolean(this.#dialog()?.open) && index === this.#lightboxIndex;
      }

      return index === this.#activeIndex;
    }

    #cancelExternalPlay(iframe) {
      const cleanup = this.#externalPlayCleanups.get(iframe);

      if (!cleanup) return;

      cleanup();
      this.#externalPlayCleanups.delete(iframe);
    }

    #pauseExternalVideo(iframe) {
      this.#cancelExternalPlay(iframe);

      const host = iframe.dataset.host || '';

      if (host === 'youtube') {
        iframe.contentWindow?.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
          '*'
        );
        return;
      }

      if (host === 'vimeo') {
        iframe.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
      }
    }

    #playExternalVideo(iframe) {
      this.#cancelExternalPlay(iframe);

      const sendPlay = () => {
        if (!this.#isExternalVideoActive(iframe)) return;

        const host = iframe.dataset.host || '';

        if (host === 'youtube') {
          iframe.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'mute', args: [] }),
            '*'
          );
          iframe.contentWindow?.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: [] }),
            '*'
          );
          return;
        }

        if (host === 'vimeo') {
          iframe.contentWindow?.postMessage(JSON.stringify({ method: 'setVolume', value: 0 }), '*');
          iframe.contentWindow?.postMessage(JSON.stringify({ method: 'play' }), '*');
        }
      };

      // Off-screen / still-loading iframes can miss the first postMessage —
      // wait for load once, then play. Revisits use the ready flag.
      if (iframe.dataset.galleryMediaReady === 'true') {
        sendPlay();
        return;
      }

      let loadTimer = 0;
      let retryTimer = 0;

      const onLoad = () => {
        iframe.dataset.galleryMediaReady = 'true';
        iframe.removeEventListener('load', onLoad);
        window.clearTimeout(retryTimer);
        loadTimer = window.setTimeout(sendPlay, 75);
      };

      iframe.addEventListener('load', onLoad);

      if (iframe.getAttribute('loading') === 'lazy') {
        iframe.loading = 'eager';
      }

      // If load already fired before we subscribed, retry play shortly and
      // mark ready so we do not keep re-entering the delayed path.
      retryTimer = window.setTimeout(() => {
        if (iframe.dataset.galleryMediaReady === 'true') return;
        if (!this.#isExternalVideoActive(iframe)) return;

        iframe.dataset.galleryMediaReady = 'true';
        iframe.removeEventListener('load', onLoad);
        sendPlay();
      }, 200);

      this.#externalPlayCleanups.set(iframe, () => {
        iframe.removeEventListener('load', onLoad);
        window.clearTimeout(loadTimer);
        window.clearTimeout(retryTimer);
      });
    }

    #activateModel(slide) {
      const modelRoot = slide?.querySelector('[data-gallery-model]');
      const viewerHost = modelRoot?.querySelector('[data-gallery-model-viewer]');

      if (!modelRoot || !viewerHost || modelRoot.classList.contains('is-active')) return;

      this.#resetImageZoom();

      const template = viewerHost.querySelector('template');

      if (template && !viewerHost.querySelector('model-viewer')) {
        viewerHost.append(template.content.cloneNode(true));
      }

      const modelViewer = viewerHost.querySelector('model-viewer');

      if (!modelViewer) return;

      const inLightbox = Boolean(slide.closest('[data-gallery-dialog]'));

      this.#ensureModelViewerStyles();
      viewerHost.hidden = false;
      modelRoot.classList.add('is-active');

      if (inLightbox) {
        this.#dialog()?.classList.add('is-model-active');
        this.#syncDialogView360(slide);
      } else {
        this.classList.add('is-model-active');
        this.#syncView360(slide);
        this.#syncStageDrag();
      }

      this.#setupModelViewerUI(modelViewer);
    }

    #deactivateModel(slide) {
      const modelRoot = slide?.querySelector('[data-gallery-model]');
      const viewerHost = modelRoot?.querySelector('[data-gallery-model-viewer]');
      const modelViewer =
        viewerHost?.querySelector('model-viewer') ||
        slide?.querySelector('model-viewer');

      if (!modelRoot) return;

      const inLightbox = Boolean(slide.closest('[data-gallery-dialog]'));
      const ui = modelViewer ? this.#modelViewerUIs.get(modelViewer) : null;

      ui?.pause?.();

      modelRoot.classList.remove('is-active');

      if (viewerHost) {
        viewerHost.hidden = true;
      }

      if (inLightbox) {
        this.#dialog()?.classList.remove('is-model-active');
        this.#syncDialogView360(this.#dialogSlides()[this.#lightboxIndex]);
      } else {
        this.classList.remove('is-model-active');
        this.#syncView360(this.#slides()[this.#activeIndex]);
        this.#syncStageDrag();
      }
    }

    #deactivateAllModels() {
      for (const slide of this.#slides()) {
        this.#deactivateModel(slide);
      }

      for (const slide of this.#dialogSlides()) {
        this.#deactivateModel(slide);
      }
    }

    #ensureModelViewerStyles() {
      const href =
        'https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css';

      if (document.querySelector(`link[href="${href}"]`)) return;

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.append(link);
    }

    #setupModelViewerUI(modelViewer) {
      if (this.#modelViewerUIs.has(modelViewer)) {
        this.#modelViewerUIs.get(modelViewer)?.play?.();
        return;
      }

      if (!window.Shopify?.loadFeatures) return;

      window.Shopify.loadFeatures([
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: (errors) => {
            if (errors || !window.Shopify?.ModelViewerUI) return;
            if (this.#modelViewerUIs.has(modelViewer)) return;

            const ui = new window.Shopify.ModelViewerUI(modelViewer);
            this.#modelViewerUIs.set(modelViewer, ui);
            ui.play?.();
          },
        },
      ]);
    }

    #zoomMediaEl(slide = this.#slides()[this.#activeIndex]) {
      if (!slide) return null;

      const type = slide.dataset.mediaType || '';

      if (type === 'video' || type === 'external_video') return null;

      if (type === 'model') {
        const modelRoot = slide.querySelector('[data-gallery-model]');

        if (modelRoot?.classList.contains('is-active')) return null;

        return modelRoot?.querySelector('.product-media-gallery__stage-image') || null;
      }

      return slide.querySelector('.product-media-gallery__stage-image');
    }

    #resetImageZoom() {
      for (const slide of this.#slides()) {
        const media =
          slide.querySelector('.product-media-gallery__stage-image') ||
          slide.querySelector('.product-media-gallery__model-poster img');

        if (media) {
          media.style.transform = '';
        }
      }

      this.#imageZoom = { scale: 1, x: 0, y: 0 };
      this.#pinch = null;
      this.#panZoom = null;
      this.classList.remove('is-image-zoomed');
      this.#syncStageDrag();
    }

    #clampImagePan() {
      const viewport = this.#viewport();

      if (!viewport || this.#imageZoom.scale <= 1.01) {
        this.#imageZoom.x = 0;
        this.#imageZoom.y = 0;
        return;
      }

      const maxX = (viewport.clientWidth * (this.#imageZoom.scale - 1)) / 2;
      const maxY = (viewport.clientHeight * (this.#imageZoom.scale - 1)) / 2;

      this.#imageZoom.x = Math.min(maxX, Math.max(-maxX, this.#imageZoom.x));
      this.#imageZoom.y = Math.min(maxY, Math.max(-maxY, this.#imageZoom.y));
    }

    #applyImageZoom() {
      const el = this.#zoomMediaEl();

      if (!el) return;

      const { scale, x, y } = this.#imageZoom;

      if (scale <= 1.01) {
        this.#imageZoom.scale = 1;
        this.#imageZoom.x = 0;
        this.#imageZoom.y = 0;
        el.style.transform = '';
        this.classList.remove('is-image-zoomed');
        this.#syncStageDrag();
        return;
      }

      el.style.transform = `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
      this.classList.add('is-image-zoomed');
      this.#syncStageDrag();
    }

    #touchDistance(touches) {
      const a = touches[0];
      const b = touches[1];

      return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
    }

    #onTouchStart = (event) => {
      if (this.#isDesktopLightbox()) return;
      if (this.classList.contains('is-model-active')) return;

      if (event.touches.length === 2) {
        if (!this.#zoomMediaEl()) return;

        event.preventDefault();
        this.#panZoom = null;
        this.#pinch = {
          startDistance: Math.max(1, this.#touchDistance(event.touches)),
          startScale: this.#imageZoom.scale,
        };
        // A pinch is not a swipe: hold the stage still while it lasts
        this.#syncStageDrag();
        return;
      }

      if (event.touches.length === 1 && this.#imageZoom.scale > 1.01) {
        const touch = event.touches[0];

        this.#panZoom = {
          startX: touch.clientX,
          startY: touch.clientY,
          originX: this.#imageZoom.x,
          originY: this.#imageZoom.y,
        };
      }
    };

    #onTouchMove = (event) => {
      if (this.#isDesktopLightbox()) return;

      if (this.#pinch && event.touches.length === 2) {
        event.preventDefault();

        const ratio = this.#touchDistance(event.touches) / this.#pinch.startDistance;
        const nextScale = Math.min(
          IMAGE_ZOOM_MAX,
          Math.max(IMAGE_ZOOM_MIN, this.#pinch.startScale * ratio)
        );

        this.#imageZoom.scale = nextScale;
        this.#clampImagePan();
        this.#applyImageZoom();
        return;
      }

      if (this.#panZoom && event.touches.length === 1 && this.#imageZoom.scale > 1.01) {
        event.preventDefault();

        const touch = event.touches[0];

        this.#imageZoom.x = this.#panZoom.originX + (touch.clientX - this.#panZoom.startX);
        this.#imageZoom.y = this.#panZoom.originY + (touch.clientY - this.#panZoom.startY);
        this.#clampImagePan();
        this.#applyImageZoom();
      }
    };

    #onTouchEnd = (event) => {
      if (event.touches.length < 2 && this.#pinch) {
        this.#pinch = null;
        this.#syncStageDrag();
      }

      if (event.touches.length === 0) {
        this.#panZoom = null;

        if (this.#imageZoom.scale <= 1.01) {
          this.#resetImageZoom();
        }
      }
    };

    #onPrev = () => {
      this.#select(this.#activeIndex - 1);
    };

    #onNext = () => {
      this.#select(this.#activeIndex + 1);
    };

    #onZoom = () => {
      if (!this.#isDesktopLightbox()) return;

      this.#openLightbox();
    };

    /**
     * Desktop: double-click the stage media to open lightbox.
     * Bound on the stage (not the active image) so we don't rebind on slide change.
     * Stage images use pointer-events: none, so events hit the viewport and bubble here.
     * Controls sit above with pointer-events: none except buttons — those are filtered out.
     */
    #onStageDblClick = (event) => {
      if (!this.#isDesktopLightbox()) return;
      if (performance.now() - this.#stageDragEndedAt < DRAG_CLICK_GUARD_MS) return;
      if (event.target.closest('button, video, iframe, model-viewer, a')) return;
      if (this.classList.contains('is-model-active')) return;

      const type = this.#slides()[this.#activeIndex]?.dataset.mediaType || '';

      if (type === 'video' || type === 'external_video') return;

      this.#openLightbox();
    };

    #openLightbox() {
      const dialog = this.#dialog();

      if (!dialog || dialog.open || this.#lightboxClosing) return;

      this.#deactivateAllModels();
      this.#pauseAllMedia();

      // Portal out of the gallery column so close doesn't flash in-flow.
      if (dialog.parentElement !== document.body) {
        document.body.append(dialog);
      }

      dialog.classList.remove('is-open', 'is-closing');
      dialog.showModal();
      this.#setLightboxIndex(this.#activeIndex, { scrollStage: false });
      this.#scrollLightboxToSlide(this.#lightboxIndex, { instant: true });
      this.#syncDialogView360(this.#dialogSlides()[this.#lightboxIndex]);

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dialog.classList.add('is-open');
          this.#dialogClose()?.focus();
        });
      });
    }

    #requestCloseLightbox() {
      const dialog = this.#dialog();

      if (!dialog?.open || this.#lightboxClosing) return;

      this.#lightboxClosing = true;
      dialog.classList.remove('is-open');
      dialog.classList.add('is-closing');

      let settled = false;

      const finish = () => {
        if (settled) return;

        settled = true;
        dialog.removeEventListener('transitionend', onEnd);
        window.clearTimeout(fallback);
        dialog.close();
      };

      const onEnd = (event) => {
        if (event.target !== dialog || event.propertyName !== 'opacity') return;

        finish();
      };

      dialog.addEventListener('transitionend', onEnd);
      const fallback = window.setTimeout(finish, 320);
    }

    #restoreDialogHome() {
      const dialog = this.#dialog();

      if (!dialog || !this.#dialogHome) return;

      if (dialog.open) {
        dialog.close();
      }

      dialog.classList.remove('is-open', 'is-closing');

      if (dialog.parentElement !== this.#dialogHome) {
        this.#dialogHome.append(dialog);
      }
    }

    #observeLightboxSlides(viewport) {
      const slides = this.#dialogSlides();

      if (slides.length < 2) return;

      this.#lightboxObserver?.disconnect();
      this.#lightboxObserver = new IntersectionObserver(this.#onLightboxIntersect, {
        root: viewport,
        threshold: [0.55, 0.75],
      });

      for (const slide of slides) {
        this.#lightboxObserver.observe(slide);
      }
    }

    #setLightboxIndex(index, { focus = false, scrollStage = true } = {}) {
      const slides = this.#dialogSlides();
      const thumbs = this.#dialogThumbsList();
      const maxIndex = Math.max(slides.length, thumbs.length, 1) - 1;
      const nextIndex = Math.max(0, Math.min(maxIndex, Math.floor(Number(index) || 0)));
      const previousIndex = this.#lightboxIndex;

      this.#lightboxIndex = nextIndex;

      for (const slide of slides) {
        slide.classList.toggle('is-active', Number(slide.dataset.index) === nextIndex);
      }

      if (previousIndex !== nextIndex) {
        this.#deactivateModel(slides[previousIndex]);
      }

      for (const thumb of thumbs) {
        const isActive = Number(thumb.dataset.index) === nextIndex;

        thumb.classList.toggle('is-active', isActive);
        thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
        thumb.tabIndex = isActive ? 0 : -1;

        if (isActive) {
          this.#alignInScroller(this.#dialogThumbs(), thumb, {
            behavior: 'auto',
            align: 'start',
          });

          if (focus) {
            thumb.focus({ preventScroll: true });
          }
        }
      }

      this.#syncDialogView360(slides[nextIndex]);
      this.#syncMediaPlayback(nextIndex, slides);

      if (scrollStage) {
        this.#scrollLightboxToSlide(nextIndex);
      }
    }

    #scrollLightboxToSlide(index, { instant = false } = {}) {
      const slide = this.#dialogSlides()[index];
      const viewport = this.#dialogViewport();

      if (!slide || !viewport) return;

      this.#lightboxIgnoreScrollSync = true;
      window.clearTimeout(this.#lightboxScrollSyncTimer);

      this.#alignInScroller(viewport, slide, {
        behavior: instant ? 'auto' : 'smooth',
      });

      const release = () => {
        this.#lightboxIgnoreScrollSync = false;
        viewport.removeEventListener('scrollend', release);
      };

      viewport.addEventListener('scrollend', release, { once: true });
      this.#lightboxScrollSyncTimer = window.setTimeout(release, instant ? 50 : 450);
    }

    #nearestLightboxSlideIndex() {
      const viewport = this.#dialogViewport();
      const slides = this.#dialogSlides();

      if (!viewport || !slides.length) return 0;

      const scrollPos = viewport.scrollTop;
      const extent = viewport.clientHeight;
      let nearest = 0;
      let nearestDistance = Number.POSITIVE_INFINITY;

      for (const slide of slides) {
        const offset = slide.offsetTop;
        const distance = Math.abs(offset - scrollPos);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = Number(slide.dataset.index) || 0;
        }

        if (scrollPos + extent / 2 >= offset && scrollPos + extent / 2 < offset + extent) {
          return Number(slide.dataset.index) || 0;
        }
      }

      return nearest;
    }

    #onLightboxIntersect = (entries) => {
      if (this.#lightboxIgnoreScrollSync || this.#lightboxDrag) return;

      let best = null;

      for (const entry of entries) {
        if (!entry.isIntersecting) continue;

        if (!best || entry.intersectionRatio > best.intersectionRatio) {
          best = entry;
        }
      }

      if (!best) return;

      const index = Number(best.target.dataset.index);

      if (Number.isNaN(index) || index === this.#lightboxIndex) return;

      this.#setLightboxIndex(index, { scrollStage: false });
    };

    #onLightboxScrollEnd = () => {
      if (this.#lightboxIgnoreScrollSync || this.#lightboxDrag) return;

      const index = this.#nearestLightboxSlideIndex();

      if (index !== this.#lightboxIndex) {
        this.#setLightboxIndex(index, { scrollStage: false });
      }
    };

    #onLightboxPointerDown = (event) => {
      if (event.pointerType !== 'mouse') return;
      if (event.button !== 0) return;
      if (event.target.closest('button, a, video, iframe, model-viewer')) return;
      if (this.#dialog()?.classList.contains('is-model-active')) return;

      const viewport = this.#dialogViewport();

      if (!viewport || this.#dialogSlides().length < 2) return;

      this.#lightboxDrag = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        scrollTop: viewport.scrollTop,
        moved: false,
      };

      viewport.classList.add('is-dragging');
      viewport.setPointerCapture(event.pointerId);
      event.preventDefault();
    };

    #onLightboxPointerMove = (event) => {
      if (!this.#lightboxDrag || event.pointerId !== this.#lightboxDrag.pointerId) return;

      const viewport = this.#dialogViewport();

      if (!viewport) return;

      const dx = event.clientX - this.#lightboxDrag.startX;
      const dy = event.clientY - this.#lightboxDrag.startY;

      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
        this.#lightboxDrag.moved = true;
      }

      viewport.scrollTop = this.#lightboxDrag.scrollTop - dy;
    };

    #onLightboxPointerUp = (event) => {
      if (!this.#lightboxDrag || event.pointerId !== this.#lightboxDrag.pointerId) return;

      const viewport = this.#dialogViewport();
      const moved = this.#lightboxDrag.moved;

      this.#lightboxDrag = null;
      viewport?.classList.remove('is-dragging');

      if (!moved || !viewport) return;

      this.#setLightboxIndex(this.#nearestLightboxSlideIndex(), { scrollStage: true });
    };

    #onDialogCloseClick = () => {
      this.#requestCloseLightbox();
    };

    #onDialogCancel = (event) => {
      event.preventDefault();
      this.#requestCloseLightbox();
    };

    #onDialogClose = () => {
      const dialog = this.#dialog();

      for (const slide of this.#dialogSlides()) {
        this.#deactivateModel(slide);
      }

      this.#pauseMediaInSlides(this.#dialogSlides());

      dialog?.classList.remove('is-open', 'is-closing', 'is-model-active');
      this.#lightboxClosing = false;
      this.#restoreDialogHome();
      this.#select(this.#lightboxIndex, { scrollStage: true });

      const zoom = this.#zoom();

      if (zoom && !zoom.hidden) {
        zoom.focus();
      } else {
        this.#viewport()?.focus();
      }
    };

    #onDialogView360 = () => {
      this.#activateModel(this.#dialogSlides()[this.#lightboxIndex]);
    };

    #onDialogThumbClick = (event) => {
      const thumb = event.target.closest('[data-gallery-dialog-thumb]');

      if (!thumb || !this.#dialog()?.contains(thumb)) return;

      this.#setLightboxIndex(Number(thumb.dataset.index));
    };

    #onDialogThumbKeydown = (event) => {
      this.#handleLightboxNavKeys(event, { focus: true });
    };

    #onDialogKeydown = (event) => {
      if (event.target.closest('[data-gallery-dialog-thumbs]')) return;
      if (event.target.closest('[data-gallery-dialog-viewport]')) return;

      this.#handleLightboxNavKeys(event, { focus: false });
    };

    #onLightboxViewportKeydown = (event) => {
      this.#handleLightboxNavKeys(event, { focus: false });
    };

    #handleLightboxNavKeys(event, { focus }) {
      const count = Math.max(this.#dialogSlides().length, this.#dialogThumbsList().length, 1);

      if (count < 2) return;

      let nextIndex = this.#lightboxIndex;

      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          nextIndex = this.#lightboxIndex - 1;
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          nextIndex = this.#lightboxIndex + 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = count - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.#setLightboxIndex(nextIndex, { focus });
    }

    #onView360 = () => {
      this.#activateModel(this.#slides()[this.#activeIndex]);
    };

    #onThumbClick = (event) => {
      const thumb = event.target.closest('[data-gallery-thumb]');

      if (!thumb || !this.contains(thumb)) return;

      this.#select(Number(thumb.dataset.index));
    };

    #onViewportKeydown = (event) => {
      this.#handleNavKeys(event, { focus: false, vertical: false });
    };

    #onThumbKeydown = (event) => {
      // A thumb column also answers Up / Down, the direction it is laid out in.
      this.#handleNavKeys(event, { focus: true, vertical: this.#isVerticalThumbs() });
    };

    #handleNavKeys(event, { focus, vertical }) {
      const slides = this.#slides();

      if (!slides.length) return;

      let nextIndex = this.#activeIndex;

      switch (event.key) {
        case 'ArrowLeft':
          nextIndex = this.#activeIndex - 1;
          break;
        case 'ArrowRight':
          nextIndex = this.#activeIndex + 1;
          break;
        case 'ArrowUp':
          if (!vertical) return;
          nextIndex = this.#activeIndex - 1;
          break;
        case 'ArrowDown':
          if (!vertical) return;
          nextIndex = this.#activeIndex + 1;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = slides.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.#select(nextIndex, { focus });
    };
  }

  customElements.define(TAG, ProductMediaGallery);
})();
